import { makeAutoObservable, runInAction } from 'mobx';

import {
  CatalogRequests,
  type CategoriesResponse,
  LandingPage,
} from '~/api/Catalog';
import { company } from '~/company/Company';
import { catalogWarehouseParam } from '~/utils/catalogWarehouse';
import { PreloadCache } from '~/utils/preloadCache';

/**
 * Represents a single hero banner item
 */
export interface HeroBannerItem {
  /** Unique identifier of the banner */
  id: number;
  /** Display name of the banner */
  name: string;
  /** Navigation link when banner is clicked */
  deepLink: string;
  /** Desktop image URL */
  previewImage: string;
  /** Mobile image URL (thumbnail) */
  previewImageThumb: string;
}

/**
 * Store for managing hero banner data on the main page.
 *
 * Instantiated per request/component via `useStoreInstance` (mirroring
 * `HomeCategories`/`ShopCategories`) so that SSR-preloaded data is read while
 * the instance is created — during render, after `preload()` has populated the
 * cache. This guarantees the banner is rendered into the server HTML instead
 * of only appearing after client hydration.
 */
export class HeroBannerStore {
  /** List of hero banner items to display */
  public items: HeroBannerItem[] = [];
  /** Whether data is currently being fetched for the first time (no items yet) */
  public isLoading = false;
  /** Whether data is being refreshed in the background while items are shown */
  public isRefreshing = false;
  /**
   * Whether the store has already settled its first data source — hydrated
   * from SSR (even to an empty banner list) or started its first client load.
   * Lets callers tell "no banner configured" apart from "not loaded yet" so
   * they don't refetch a confirmed-empty banner on every mount.
   */
  public isInitialized = false;

  constructor(serverWarehouse?: string, serverLang?: string) {
    if (serverWarehouse) {
      this.init(serverWarehouse, serverLang);
    }

    makeAutoObservable(this);
  }

  /** SSR preloaded data cache, keyed by warehouse + language */
  public static preloadedData = new PreloadCache<CategoriesResponse>({
    max: 500,
    ttlMs: 60_000,
  });

  /**
   * Preloads hero banner data during SSR
   * @param warehouse - Warehouse code
   * @param serverLang - Optional server language
   * @returns Categories response or null if disabled/failed
   */
  public static async preload(
    warehouse: string,
    serverLang?: string,
  ): Promise<CategoriesResponse | null> {
    if (!company.newDesignSystemEnabled) {
      return null;
    }

    try {
      const data = await this.request(warehouse, serverLang);
      this.preloadedData.set(this.getCategoriesId(warehouse, serverLang), data);
      return data;
    } catch {
      return null;
    }
  }

  public static removePreloadedData(id: string) {
    this.preloadedData.delete(id);
  }

  public static getCategoriesId(warehouse: string, serverLang?: string) {
    return `${warehouse}.${serverLang}`;
  }

  private static request(warehouse: string, serverLang?: string) {
    return CatalogRequests.getCategories(
      {
        ...catalogWarehouseParam(warehouse),
        landingPage: [LandingPage.HERO_BANNER],
        includes: { empty: true },
        page: { size: 25, current: 1 },
      },
      serverLang,
    );
  }

  /** Whether to display the hero banner (has items and feature enabled) */
  public get shouldShow(): boolean {
    return company.newDesignSystemEnabled && this.items.length > 0;
  }

  /** Whether to show the banner section (loading skeleton or banner) */
  public get shouldShowSection(): boolean {
    return (
      company.newDesignSystemEnabled &&
      (this.isLoading || this.items.length > 0)
    );
  }

  /**
   * Whether to render the loading skeleton.
   * Only true on a cold load (no items yet) — a background refresh keeps the
   * existing banner on screen instead of flashing the skeleton.
   */
  public get shouldShowSkeleton(): boolean {
    return (
      company.newDesignSystemEnabled &&
      this.isLoading &&
      this.items.length === 0
    );
  }

  /**
   * Loads hero banner data on the client side
   * @param warehouse - Warehouse code
   * @param serverLang - Optional server language
   */
  public async load(warehouse: string, serverLang?: string): Promise<void> {
    if (!company.newDesignSystemEnabled) {
      return;
    }

    const hasItems = this.items.length > 0;

    runInAction(() => {
      this.isInitialized = true;
      if (hasItems) {
        this.isRefreshing = true;
      } else {
        this.isLoading = true;
      }
    });

    try {
      const response = await HeroBannerStore.request(warehouse, serverLang);
      runInAction(() => this.setItems(response));
    } catch {
      // On a background refresh keep the currently displayed banner; only
      // clear items when there was nothing to show in the first place.
      if (!hasItems) {
        runInAction(() => this.setItems(null));
      }
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isRefreshing = false;
      });
    }
  }

  private init(serverWarehouse: string, serverLang?: string) {
    const isSSR = typeof window === 'undefined';
    const ssrDegraded = !isSSR && window.__INITIAL_STATE__?.ssrDegraded;
    const preloadedData = ssrDegraded
      ? null
      : (!isSSR ? window.__INITIAL_STATE__?.heroBanners : null) ||
        HeroBannerStore.preloadedData.get(
          HeroBannerStore.getCategoriesId(serverWarehouse, serverLang),
        );

    if (!preloadedData) {
      // Degraded SSR hydration or CSR without data. Do NOT start loading here:
      // init() runs during the render pass (store is created in useState), and
      // flipping isLoading synchronously would render the skeleton on the first
      // client render while the server emitted none — a hydration mismatch that
      // crashes the whole root. The client fetch is triggered post-mount by the
      // `useOnWarehouseChange` effect in Root (guarded by `!isInitialized`).
      return;
    }

    this.setItems(preloadedData);
    this.isInitialized = true;
  }

  private setItems(response: CategoriesResponse | null) {
    this.items = response ? this.convert(response) : [];
  }

  /**
   * Converts the raw categories response into the flat list of hero banner
   * items. Public and static so SSR entry points can build preload links
   * from the same preloaded data without instantiating the store.
   */
  public static toItems(response: CategoriesResponse | null): HeroBannerItem[] {
    if (!response) {
      return [];
    }
    return response.data
      .flatMap((item) => item.children ?? [])
      .filter((child) => child.category.images.length === 2)
      .map((child) => ({
        id: child.category.id,
        name: child.category.name,
        deepLink: child.category.previewText ?? '',
        previewImage: child.category.images[0]?.url ?? '',
        previewImageThumb: child.category.images[1]?.url ?? '',
      }));
  }

  private convert(response: CategoriesResponse): HeroBannerItem[] {
    return HeroBannerStore.toItems(response);
  }
}
